// GENERATED — DO NOT EDIT THIS COPY.
// Source: civitas-trust-registry/packages/stale-build/staleBuild.ts
// Update: edit the source, then `node packages/stale-build/sync.mjs`.
// A drift test in this repo fails if this file is edited in place.
// The stale-build guard: what an app does when the build it is running was
// replaced under it (stale-build-recovery T2).
//
// A production build names every chunk by content hash and DELETES the old
// files when it is replaced. Any tab that was open across a deploy therefore
// asks for a chunk that no longer exists, and the SPA fallback answers that
// request with index.html and a 200 — so the browser is handed HTML where it
// expected JavaScript and the router shows «Unexpected Application Error!».
// Seen on prod 2026-09-07, on the admin registry page, right after a deploy.
//
// The rule this file exists to enforce: **reload at most once per window,
// and never loop.** A recovery that reloads unconditionally turns a genuine
// missing chunk (a broken deploy) into an infinite refresh, which is worse
// than the error it replaces — the person can no longer read the message or
// leave the page.

/// How long a single window remembers that it already tried. Long enough to
/// cover a reload plus a slow first paint, short enough that a SECOND deploy
/// half an hour later is still recovered automatically.
export const RETRY_WINDOW_MS = 60_000;

export const STORAGE_KEY = 'civitas.staleBuild.recoveredAt';

/// Is this the failure of a build that moved, rather than an ordinary error?
///
/// Four shapes, all seen in the wild:
///   · webpack's own `ChunkLoadError` (name or message);
///   · the same as plain text, including the CSS variant;
///   · the ESM phrasing used by Vite and by native dynamic import;
///   · the SyntaxError a browser raises when the SPA fallback hands it
///     index.html and it starts parsing `<!doctype html>` as JavaScript.
export function isStaleBuildError(error: unknown): boolean {
  if (!error) return false;
  const err = error as {name?: unknown; message?: unknown};
  const name = typeof err.name === 'string' ? err.name : '';
  const message = typeof err.message === 'string' ? err.message : String(error);
  if (name === 'ChunkLoadError') return true;
  return (
    /loading (css )?chunk [^ ]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    (name === 'SyntaxError' && /unexpected token ['"`]?</i.test(message))
  );
}

export interface RecoveryDeps {
  /// Per-window memory. `sessionStorage` in a browser; anything with the same
  /// two methods in a test. A browser that refuses storage (private mode,
  /// blocked cookies) must not break the guard: absent storage means «cannot
  /// remember», and the guard then declines to reload rather than risk a loop.
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  now?: () => number;
  log?: (message: string) => void;
}

export type Recovery = 'reloading' | 'already-tried' | 'cannot-remember' | 'not-stale';

/// Decide, and act. Returns what was decided so a caller can render the
/// honest thing: a spinner while the page reloads, or the error itself when
/// reloading is not the answer.
export function recoverFromStaleBuild(error: unknown, deps: RecoveryDeps): Recovery {
  if (!isStaleBuildError(error)) return 'not-stale';
  const now = deps.now ? deps.now() : Date.now();
  const storage = deps.storage;
  if (!storage) {
    deps.log?.('stale build detected, but this browser keeps no session storage — not reloading, to avoid a loop');
    return 'cannot-remember';
  }
  let last = 0;
  try {
    last = Number(storage.getItem(STORAGE_KEY) || 0);
  } catch {
    deps.log?.('stale build detected, but session storage could not be read — not reloading, to avoid a loop');
    return 'cannot-remember';
  }
  if (last && now - last < RETRY_WINDOW_MS) {
    deps.log?.('stale build detected again right after a reload — the build on the server is incomplete, leaving the error visible');
    return 'already-tried';
  }
  try {
    storage.setItem(STORAGE_KEY, String(now));
  } catch {
    return 'cannot-remember';
  }
  deps.log?.('the app was replaced by a new build; reloading once to pick it up');
  deps.reload();
  return 'reloading';
}

/// Catch the failures that never reach a router: a lazy import started
/// outside routing, or a chunk requested by an already-mounted component.
/// Returns a function that removes the listeners again (tests, hot reload).
export function installStaleBuildGuard(deps: RecoveryDeps, target: any = typeof window === 'undefined' ? null : window): () => void {
  if (!target || typeof target.addEventListener !== 'function') return () => undefined;
  const onError = (e: any) => { recoverFromStaleBuild(e?.error ?? e?.message, deps); };
  const onRejection = (e: any) => { recoverFromStaleBuild(e?.reason, deps); };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}

/// The browser wiring, in one call: `installStaleBuildGuard(browserDeps())`.
export function browserDeps(): RecoveryDeps {
  return {
    storage: (() => { try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; } })(),
    reload: () => window.location.reload(),
    log: (m) => console.warn(`[stale-build] ${m}`),
  };
}
